import { DocumentMapping } from "@/configuration/document-mappings/types";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { Classification } from "@/src/models/constants/classifications";

// Confluent - All Star (All Star Physical Therapy) fax-routing document intake, on Clinicient.
// Derived from the customer's "Fax Routing" tab. Titles use the sheet's document-type names
// (static — no template vars yet); case-matching field extraction and the runtime workflow
// branches land in later phases:
//   - CARE_PLAN signed/unsigned split (signed = upload+attach; unsigned = task + chart note +
//     fax-to-sender) is a workflow-steps getEffectiveWorkflowType override.
//   - MEDICAL_RECORD_REQUEST provider note-request (this entry) vs. 3rd-party records request
//     (ShareCare CloudCruise workflow, not yet built) is likewise a runtime branch.
// `folder` carries the sheet's Clinicient attachment type verbatim; the folder-name -> Clinicient
// document-type slug map is added with the ClinicientIntegration.
export const CONFLUENT_ALL_STAR_MAPPINGS: Partial<Record<Classification, DocumentMapping>> = {
  [Classification.CARE_PLAN]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Plan of Care",
    documentLabel: "Plan of Care",
    folder: "Plan of Care Approval",
    folderMapping: "Plan of Care Approval",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.CARE_PLAN,
    group: ConfigSubdomain.CONFLUENT_ALL_STAR,
  },
  [Classification.PROGRESS_NOTE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Medical Provider Note",
    documentLabel: "Medical Provider Note",
    folder: "Case Record",
    folderMapping: "Case Record",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PROGRESS_NOTE,
    group: ConfigSubdomain.CONFLUENT_ALL_STAR,
  },
  [Classification.PROGRESS_NOTE_PRE_OPERATIVE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Operative Note",
    documentLabel: "Operative Note",
    folder: "Case Record",
    folderMapping: "Case Record",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PROGRESS_NOTE_PRE_OPERATIVE,
    group: ConfigSubdomain.CONFLUENT_ALL_STAR,
  },
  [Classification.OPERATION_NOTE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Operative Note",
    documentLabel: "Operative Note",
    folder: "Case Record",
    folderMapping: "Case Record",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.OPERATION_NOTE,
    group: ConfigSubdomain.CONFLUENT_ALL_STAR,
  },
  [Classification.MEDICAL_RECORD_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Task Type: Note Requested", "Task Note: Please fulfill note request in attachments"],
    assignedTo: "Casaga, Traci",
    displayName: "Note Request",
    documentLabel: "Note Request",
    folder: "Case Record",
    folderMapping: "Case Record",
    inputDate: "Date Received",
    instructions: null,
    priority: "Medium",
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.MEDICAL_RECORD_REQUEST,
    group: ConfigSubdomain.CONFLUENT_ALL_STAR,
  },
  [Classification.REFERRAL_STATUS_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Task Type: Information Required", "Task Note: Please send status update to requester"],
    assignedTo: "Front Desk",
    displayName: "Appointment Status Update Request",
    documentLabel: "Appointment Status Update Request",
    folder: "Case Record",
    folderMapping: "Case Record",
    inputDate: "Date Received",
    instructions: null,
    priority: "Medium",
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.REFERRAL_STATUS_REQUEST,
    group: ConfigSubdomain.CONFLUENT_ALL_STAR,
  },
  [Classification.LEGAL_SUBPOENA]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Subpoena",
    documentLabel: "Subpoena",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "External Routing WF",
    classification: Classification.LEGAL_SUBPOENA,
    group: ConfigSubdomain.CONFLUENT_ALL_STAR,
  },
  // Payer / insurance bulletins — non-patient-specific updates from payers/health plans (coverage
  // policies, medical-necessity criteria, coding/benefit changes) addressed to the practice. Captured
  // under ADMIN (see classification-prompts.ts). Not on the original Fax Routing tab. `folder` is null
  // since there is no EHR upload; the document is email-forwarded to All Star and never synced to
  // Clinicient (see CONFLUENT_ALL_STAR_EMAIL_ONLY_CLASSIFICATIONS / getConfluentAllStarEmailRecipients
  // in routing.ts). A patient-lookup task still runs (DOC_MANAGEMENT workflow) but is inert — these
  // documents never have an associated patient.
  [Classification.ADMIN]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Insurance Bulletin",
    documentLabel: "Insurance Bulletin",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "External Routing WF",
    classification: Classification.ADMIN,
    group: ConfigSubdomain.CONFLUENT_ALL_STAR,
  },
  [Classification.ADMIN_CREDENTIAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Credentialing",
    documentLabel: "Credentialing",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "External Routing WF",
    classification: Classification.ADMIN_CREDENTIAL,
    group: ConfigSubdomain.CONFLUENT_ALL_STAR,
  },
  [Classification.ADMIN_RISK_ASSESSMENT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Insurance Risk Assessment",
    documentLabel: "Insurance Risk Assessment",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "External Routing WF",
    classification: Classification.ADMIN_RISK_ASSESSMENT,
    group: ConfigSubdomain.CONFLUENT_ALL_STAR,
  },
  [Classification.MEDICAL_RECORD_REQUEST_HEDIS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Insurance Records Request",
    documentLabel: "Insurance Records Request",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "External Routing WF",
    classification: Classification.MEDICAL_RECORD_REQUEST_HEDIS,
    group: ConfigSubdomain.CONFLUENT_ALL_STAR,
  },
  [Classification.LEGAL_GRIEVANCE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Patient Grievance",
    documentLabel: "Patient Grievance",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "External Routing WF",
    classification: Classification.LEGAL_GRIEVANCE,
    group: ConfigSubdomain.CONFLUENT_ALL_STAR,
  },
  [Classification.JUNK]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Duplicate",
    documentLabel: "",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Delete",
    classification: Classification.JUNK,
    group: ConfigSubdomain.CONFLUENT_ALL_STAR,
  },
  [Classification.INCOMPLETE_CANNOT_LINK_TO_PATIENT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Missing PHI",
    documentLabel: "",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Delete",
    classification: Classification.INCOMPLETE_CANNOT_LINK_TO_PATIENT,
    group: ConfigSubdomain.CONFLUENT_ALL_STAR,
  },
  [Classification.REFERRAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Referral",
    documentLabel: "Referral",
    folder: "Referral",
    folderMapping: "Referral",
    inputDate: "Date Received",
    instructions: null,
    priority: "Normal",
    workflowAction: null,
    workflowType: "Inbound Referral WF",
    classification: Classification.REFERRAL,
    group: ConfigSubdomain.CONFLUENT_ALL_STAR,
  },
  // Insurance correspondence not tied to an authorization. When the patient exists in Clinicient the
  // document is attached to a matched/Default case and a Front Desk task is created; when the patient
  // is not found it is emailed to All Star (see workflows/index.ts insurance-communication branch).
  [Classification.INSURANCE_COMMUNICATION]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Insurance Communication",
    documentLabel: "Insurance Communication",
    folder: "Case Record",
    folderMapping: "Case Record",
    inputDate: "Date Received",
    instructions: null,
    priority: "Normal",
    workflowAction: null,
    workflowType: "Insurance Communication WF",
    classification: Classification.INSURANCE_COMMUNICATION,
    group: ConfigSubdomain.CONFLUENT_ALL_STAR,
  },
} as const;
